import React, { FC } from 'react';
import { Box, Grid, Link, Skeleton, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useNavigate } from 'react-router';
import { AppTheme } from '../../settings/appTheme';
import { useRoutesHelper } from '../../helpers/routesHelper';
import { GenreType } from '../../core/models/events';
import { GenreCarouselSkeleton } from '../skeletons/genreCarouselSkeleton';

const useStyles = makeStyles<void, 'gridTitleImage' | 'gradientImg'>()((_theme, _params, classes) => ({
  box: {
    overflowX: 'scroll',
    overflowY: 'hidden',
    '&::-webkit-scrollbar': {
      height: `${AppTheme.scrollbarHeight}`,
    },
  },
  gridList: {
    flexWrap: 'nowrap',
    // Promote the list into his own layer on Chrome. This cost memory but helps keeping high FPS.
    transform: 'translateZ(0)',
    margin: '0px',
  },
  gridTitle: {
    cursor: 'pointer',
    width: 157,
    height: 'min-content',
    marginRight: 10,
    marginBottom: 20,
  },
  gridTitleImage: {
    height: '105px',
    width: '144px',
    objectFit: 'cover',
    borderRadius: '8px',
    boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.3)',
  },
  titleText: {
    fontSize: 16,
    lineHeight: '16px',
    marginTop: 10,
  },
  gridImg: {
    position: 'relative',
    '&:hover': {
      [`& .${classes.gridTitleImage}, .${classes.gradientImg}`]: {
        filter: 'brightness(60%)',
      },
    },
  },
  gradientImg: {
    position: 'absolute',
    width: '144px',
    height: '42px',
    left: '0px',
    bottom: '5px',
    background: 'linear-gradient(180deg, rgba(0, 0, 0, 0) 0%, #000000 100%)',
    opacity: 0.7,
    borderRadius: '0px 0px 8px 8px',
  },
  linkStyle: {
    '&:hover': {
      textDecoration: 'none',
      color: 'rgb(178, 178, 178)',
      filter: 'brightness(60%)',
    },
  },
  titleUnderline: {
    textDecoration: 'underline',
    textUnderlineOffset: '3px',
    textDecorationColor: 'rgba(255,255,255,0.4)',
  },
}));

export const GenreCarousel: FC<GenreCarouselType> = ({ genres, isLoading }) => {
  const navigate = useNavigate();

  const { classes } = useStyles();
  const { getGenreEventsRoute } = useRoutesHelper();

  return (
    <Box className={`carousel ${classes.box}`}>
      <Grid container className={classes.gridList}>
        {genres.length > 0 &&
          !isLoading &&
          genres.map((item) => (
            <Grid item key={item.slug} className={classes.gridTitle}>
              <Link className={classes.linkStyle} onClick={() => navigate(getGenreEventsRoute(item.slug))}>
                <Grid container direction="row">
                  <Grid item lg={12} xs={12} sm={12} md={12} xl={12}>
                    <div className={classes.gridImg}>
                      <img src={item.imageUrl} alt={item.title} className={classes.gridTitleImage} />
                      <div className={classes.gradientImg} />
                    </div>
                  </Grid>
                  <Grid item lg={12} xs={12} sm={12} md={12} xl={12}>
                    <Typography className={`${classes.titleText} ${classes.titleUnderline}`}>{item.title}</Typography>
                  </Grid>
                </Grid>
              </Link>
            </Grid>
          ))}

        {isLoading && <GenreCarouselSkeleton />}
      </Grid>
    </Box>
  );
};

type GenreCarouselType = {
  genres: GenreType[];
  isLoading: boolean;
};
